import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import { toast } from "sonner";
import { env } from "./env";

export const api = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 12_000,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // Attach the Cognito access token on every request. fetchAuthSession returns
  // cached tokens and only hits the network when refresh is required, so this
  // is cheap to call per request.
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
  } catch {
    // No active session - request goes out anonymous. Protected routes will 401.
  }
  return config;
});

let signOutHandler: (() => Promise<void> | void) | null = null;
export function registerSignOutHandler(handler: () => Promise<void> | void): void {
  signOutHandler = handler;
}

const AI_MESSAGE_PATHS = new Set([
  "/chat/message",
  "/parent/assistant/message",
]);

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<{ status_message?: string; message?: string; code?: string }>) => {
    const status = error.response?.status;
    const message =
      error.response?.data?.status_message ||
      error.response?.data?.message ||
      error.message;
    const handledByAiSurface = Boolean(
      error.response?.data?.code &&
      AI_MESSAGE_PATHS.has(error.config?.url || ""),
    );

    if (status === 401) {
      // Session is no longer valid. Sign out locally and let the route guard
      // route the user to /auth/login on the next render.
      if (signOutHandler) await signOutHandler();
    } else if (status === 403 && !handledByAiSurface) {
      toast.error(message || "You don't have access to that.");
    } else if (status === 429 && !handledByAiSurface) {
      toast.warning(message || "Slow down a moment - too many requests.");
    } else if (status && status >= 500 && !handledByAiSurface) {
      toast.error("Something went wrong on our side. Please try again.");
    }

    return Promise.reject(error);
  },
);

export function unwrap<T>(data: { status?: string; data?: T } | T): T {
  if (data && typeof data === "object" && "status" in data && "data" in data) {
    return (data as { data: T }).data;
  }
  return data as T;
}
